import {
  useEffect,
  useRef,
  useState,
  ChangeEvent,
  DragEvent,
  Fragment,
  MouseEvent,
  ReactNode,
} from "react";
import { useLazyQuery } from "@apollo/client";
import { useHistory, useLocation } from "react-router-dom";
import { HiX, HiChevronLeft } from "react-icons/hi";
import {
  TbBackpack,
  TbCoin,
  TbFilter,
  TbGhost,
  TbCrosshair,
  TbFileImport,
  TbBulb,
  TbChevronDown,
  TbChevronRight,
  TbAlertTriangle,
  TbBrandApple,
  TbCheck,
  TbBrandUbuntu,
  TbBrandWindows,
  TbFileText,
  TbFolderOpen,
  TbUpload,
  TbLayoutDashboard,
  TbLayoutNavbar,
  TbPin,
  TbRadar2,
  TbSkull,
} from "react-icons/tb";

import { analytics } from "MVVM/Analytics";
import cs2Logo from "MVVM/Assets/GameLogos/cs2.svg";
import maskHealthAmmo from "MVVM/Assets/Cs2Hud/health_ammo.png";
import maskMoney from "MVVM/Assets/Cs2Hud/money.png";
import maskEquipment from "MVVM/Assets/Cs2Hud/equipment.png";
import { ICs2Hud, TCs2CrosshairMode } from "MVVM/@types/user";
import {
  useCs2Hud,
  CS2_HUD_COLORS,
  crosshairCodeError,
} from "MVVM/Hooks/useCs2Hud";
import { useCs2HudSettingsModal } from "MVVM/Hooks/useClipQualitySettingsModal";
import { useClipQualityConfigs } from "MVVM/Hooks/useClipQualityConfigs";
import {
  matchQualityConfig,
  TQualityMatch,
} from "MVVM/Utilities/Cs2ConfigImport/matchQuality";
import {
  configLines,
  countImportedSettings,
  parseCs2Config,
  IParsedCs2Config,
} from "MVVM/Utilities/Cs2ConfigImport";
import { uploadFileToS3 } from "MVVM/Utilities/MediaUpload";
import { CS2_CONFIG_UPLOAD_URL } from "MVVM/GraphQL/user";
import {
  AllSettingsLink,
  ClearButton,
  ImportActions,
  ImportArea,
  ImportFileButton,
  DropHint,
  DropTitle,
  DropZone,
  FileName,
  FileRow,
  TipBanner,
  TipAction,
  TipLabel,
  LoadedFiles,
  MatchedConvars,
  MatchedEmpty,
  MatchedLabel,
  MatchedRow,
  MatchedValue,
  LocationOs,
  LocationPath,
  LocationNote,
  LocationId,
  LocationRow,
  PasteToggle,
  ImportButton,
  ImportResult,
  ImportSection,
  RangeControls,
  RangeInput,
  RangeRow,
  RangeValue,
  Select,
  SectionIntro,
  CloseButton,
  CodeInput,
  CodeField,
  CodeHint,
  CollapseHeader,
  Divider,
  SectionHeader,
  Toggle,
  ToggleKnob,
  ColorGrid,
  ColorSwatch,
  Controls,
  Footer,
  GameLogo,
  Header,
  HeaderText,
  Layout,
  ModalContainer,
  ModalOverlay,
  NavItem,
  OptionRow,
  PresetButton,
  PresetRow,
  Preview,
  PreviewColumn,
  PreviewLayer,
  PreviewCaveat,
  PreviewNote,
  PreviewStage,
  PreviewTint,
  RadioDot,
  SaveButton,
  SaveFeedback,
  SettingDescription,
  SettingInfo,
  SettingLabel,
  SettingRow,
  TabBar,
  Subtitle,
  Title,
} from "./styledComponents";

interface ICs2HudSettingsModal {
  onClose: () => void;
  onAllSettings?: () => void;
}

type TSection = "HUD" | "CROSSHAIR" | "IMPORT";

const SECTIONS: { id: TSection; label: string; icon: ReactNode }[] = [
  { id: "HUD", label: "HUD", icon: <TbLayoutDashboard size={18} /> },
  { id: "CROSSHAIR", label: "Crosshair", icon: <TbCrosshair size={18} /> },
  { id: "IMPORT", label: "Import", icon: <TbFileImport size={18} /> },
];

// Config files only. A folder selection hands over everything in it, and the
// budget stops a stray pick of something huge from locking up the tab.
const CONFIG_FILE = /\.(vcfg|cfg|txt)$/i;
const MAX_CONFIG_FILES = 20;
const MAX_CONFIG_BYTES = 2_000_000;

// Reads back an imported convar the way the settings above name it, rather
// than echoing the raw line from the file.
const CONVAR_READBACK: Record<
  string,
  { label: string; value: (n: number) => string }
> = {
  cl_hud_color: {
    label: "HUD color",
    value: (n) => CS2_HUD_COLORS[n]?.name ?? `${n}`,
  },
  cl_teamcounter_playercount_instead_of_avatars: {
    label: "Top panel",
    value: (n) => (n >= 1 ? "Collapsed" : "Expanded"),
  },
  cl_hud_radar_scale: { label: "Radar size", value: (n) => n.toFixed(2) },
  cl_radar_scale: { label: "Radar zoom", value: (n) => n.toFixed(2) },
  hud_scaling: { label: "HUD scale", value: (n) => n.toFixed(2) },
};

const CONFIG_FILES = [
  "cs2_user_convars_0_slot0.vcfg",
  "cs2_machine_convars.vcfg",
  "cs2_video.txt",
];

// Only the matching row is shown by default; the rest are a click away. Three
// paths above the drop zone pushed the actual drop target off screen.
const detectOs = () => {
  const ua = navigator.userAgent;
  if (/Win/i.test(ua)) return "Windows";
  if (/Mac/i.test(ua)) return "macOS";
  if (/Linux|X11/i.test(ua)) return "Linux";

  return null;
};

const PATH_PLACEHOLDER = "<yourId>";

const withPlaceholder = (path: string) => {
  const [before, after] = path.split(PATH_PLACEHOLDER);
  return (
    <>
      {before}
      <LocationId>{PATH_PLACEHOLDER}</LocationId>
      {after}
    </>
  );
};

const CONFIG_LOCATIONS = [
  {
    os: "Windows",
    icon: <TbBrandWindows size={16} />,
    path: "C:\\Program Files (x86)\\Steam\\userdata\\<yourId>\\730\\local\\cfg\\",
  },
  {
    os: "macOS",
    icon: <TbBrandApple size={16} />,
    path: "~/Library/Application Support/Steam/userdata/<yourId>/730/local/cfg/",
  },
  {
    os: "Linux",
    icon: <TbBrandUbuntu size={16} />,
    path: "~/.steam/steam/userdata/<yourId>/730/local/cfg/",
  },
];

export const CROSSHAIR_MODES: {
  id: TCs2CrosshairMode;
  label: string;
  description: string;
}[] = [
  {
    id: "PLAYER",
    label: "My crosshair",
    description: "Your in-game crosshair, pulled from the demo",
  },
  {
    id: "ALLSTAR",
    label: "Allstar crosshair",
    description: "Our clean default, the same on every clip you make",
  },
  {
    id: "CODE",
    label: "Custom crosshair",
    description: "Paste your CS2 share code",
  },
  {
    id: "HIDDEN",
    label: "No crosshair",
    description: "No crosshair at all on your clips",
  },
];

const PREVIEW_ZOOM = 2.4;

const SHOTS = `${process.env.REACT_APP_S3_CLIP_BUCKET}/static/web/customization`;
export const HUD_ON = `${SHOTS}/hud_all.png`;
export const HUD_OFF = `${SHOTS}/hud_disabled.png`;
const HUD_TOP_EXPANDED = `${SHOTS}/hud_top_expanded.png`;

// clip-path inset(), as top/right/bottom/left percentages of the 1920x1080 frame.
// All three shots share one camera, so a clipped layer swaps only its own element.
export const REGIONS = {
  radar: "0.8% 83.6% 69.2% 1.1%",
  money: "91.3% 87.6% 0% 0.9%",
  equipment: "67.6% 0% 11.5% 88.7%",
  chat: "70.9% 75.2% 5.9% 0%",
  killfeed: "5.1% 0% 89.7% 74.2%",
  healthAmmo: "90.1% 26.2% 0% 29.8%",
  topCollapsed: "0% 44% 90.9% 42%",
  topExpanded: "0% 30.9% 84.6% 32%",
};

// Presets only touch the HUD elements. Color and kill feed are left alone,
// kill feed because it applies with the HUD off.
const HUD_PRESETS: { label: string; values: Partial<ICs2Hud> }[] = [
  {
    label: "Minimal",
    values: {
      showHud: true,
      topPanel: "COLLAPSED",
      radar: false,
      money: false,
      equipment: false,
      chat: false,
    },
  },
  {
    label: "Everything",
    values: {
      showHud: true,
      topPanel: "EXPANDED",
      radar: true,
      money: true,
      equipment: true,
      chat: true,
    },
  },
];

interface IRange {
  label: string;
  description?: string;
  value?: number | null;
  /** Slider position while the setting is unset, so the handle starts somewhere sane. */
  unset: number;
  min: number;
  max: number;
  step: number;
  disabled?: boolean;
  /** Viewmodel rows reset as a group instead, so they opt out. */
  resettable?: boolean;
  onChange: (value: number | null) => void;
}

interface IElement {
  keys: (keyof ICs2Hud)[];
  label: string;
  icon: ReactNode;
  description?: string;
  alwaysEnabled?: boolean;
  dependsOn?: keyof ICs2Hud; // greyed out unless this one is on
}

// Kill feed sits on its own above the HUD sections, since it applies either way.
const KILLFEED: IElement = {
  keys: ["killfeed"],
  label: "Show kill feed",
  icon: <TbSkull size={18} />,
  alwaysEnabled: true,
};

const XRAY: IElement = {
  keys: ["xray"],
  label: "See through walls",
  icon: <TbGhost size={18} />,
  description: "Enemy outlines while they are behind cover",
  alwaysEnabled: true,
};

const KILLFEED_OPTIONS: IElement[] = [
  {
    keys: ["onlyMyKills"],
    label: "Only my kills",
    icon: <TbFilter size={18} />,
    description: "Hides kills you were not involved in",
    dependsOn: "killfeed",
  },
  {
    keys: ["pinnedKillfeed"],
    label: "Keep on screen",
    icon: <TbPin size={18} />,
    description: "Entries stay up instead of fading after a few seconds",
    dependsOn: "killfeed",
  },
];

const ELEMENTS: IElement[] = [
  { keys: ["radar"], label: "Radar", icon: <TbRadar2 size={18} /> },
  {
    keys: ["money", "chat"],
    label: "Money & chat",
    icon: <TbCoin size={18} />,
    description: "The game draws these in one panel, so they toggle together",
  },
  { keys: ["equipment"], label: "Equipment", icon: <TbBackpack size={18} /> },
];

export const Cs2HudSettingsModal = ({
  onClose,
  onAllSettings,
}: ICs2HudSettingsModal) => {
  const {
    draft,
    setDraft,
    crosshair,
    setCrosshair,
    isDirty,
    saving,
    saveStatus,
    saveError,
    failSave,
    save,
    isLoggedIn,
  } = useCs2Hud();
  // A deep link (/clip-settings?import) can land straight on a tab.
  const { section: linkedSection } = useCs2HudSettingsModal();
  const [section, setSection] = useState<TSection>(
    (linkedSection as TSection) ?? "HUD",
  );
  const history = useHistory();
  const { pathname, search } = useLocation();

  // Keep /clip-settings?import in step with the tab, so the URL stays
  // shareable. Scoped to that route: the modal opens from several places and
  // should not rewrite the URL of a clip page.
  useEffect(() => {
    if (pathname !== "/clip-settings") return;

    const wanted = section === "IMPORT" ? "?import" : "";
    if (search === wanted) return;

    history.replace(`${pathname}${wanted}`);
  }, [section, pathname, search, history]);
  const [pasted, setPasted] = useState("");
  const [imported, setImported] = useState<IParsedCs2Config | null>(null);
  const [fileNote, setFileNote] = useState("");
  const [dragging, setDragging] = useState(false);
  const [quality, setQuality] = useState<TQualityMatch | null>(null);
  const { configs, updateConfig, currentConfig, refetch, TIER_DISPLAY } =
    useClipQualityConfigs();
  const [showDetails, setShowDetails] = useState(false);
  const [allOs, setAllOs] = useState(false);
  const [showWhere, setShowWhere] = useState(false);
  const [loaded, setLoaded] = useState<string[]>([]);
  const stage = useRef<HTMLDivElement>(null);

  useEffect(() => {
    analytics.track("CS2 HUD - Modal Opened");
  }, []);

  // Magnify toward the cursor. Written straight to the node so moving the mouse
  // does not re-render the modal.
  const zoomToCursor = (e: MouseEvent<HTMLDivElement>) => {
    if (!stage.current) return;

    const box = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * 100;
    const y = ((e.clientY - box.top) / box.height) * 100;
    stage.current.style.transformOrigin = `${x}% ${y}%`;
    stage.current.style.transform = `scale(${PREVIEW_ZOOM})`;
  };

  const clearZoom = () => {
    if (stage.current) stage.current.style.transform = "";
  };

  const tint = draft.color > 0 ? CS2_HUD_COLORS[draft.color]?.swatch : null;

  const importedCount = imported ? countImportedSettings(imported) : 0;

  const [getConfigUploadUrl] = useLazyQuery(CS2_CONFIG_UPLOAD_URL, {
    fetchPolicy: "network-only",
  });

  const archiveConfig = async (text: string) => {
    try {
      const lines = configLines(text);
      if (!lines.length) return;

      const { data, error } = await getConfigUploadUrl();
      const presigned = data?.me?.mediaUploadUrl;
      if (!presigned) {
        analytics.track("CS2 HUD - Config Archive Failed", {
          error: error?.message ?? "no presigned post",
        });
        return;
      }

      await uploadFileToS3(
        presigned,
        new Blob([lines.join("\n")], { type: "text/plain" }),
      );
      analytics.track("CS2 HUD - Config Archived", {
        lines: lines.length,
        key: presigned.key,
      });
    } catch (error) {
      console.error("CS2 config archive failed", error);
      analytics.track("CS2 HUD - Config Archive Failed", {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  const applyConfig = (text: string) => {
    const parsed = parseCs2Config(text);
    setImported(parsed);
    archiveConfig(text);

    // A file we can read nothing from leaves everything alone. Clearing the
    // settings someone already has would be a strange reading of "no results".
    if (countImportedSettings(parsed) === 0) {
      analytics.track("CS2 HUD - Config Import Empty", {
        lines: configLines(text).length,
      });
      return;
    }

    setDraft({ ...draft, ...parsed.hud });
    // Clip quality is a separate setting from the HUD config, matched against
    // what this user's tier actually allows.
    setQuality(
      parsed.resolution ? matchQualityConfig(parsed.resolution, configs) : null,
    );
    // A config with a crosshair in it means they want theirs on their clips.
    if (parsed.crosshair)
      setCrosshair({ ...crosshair, mode: parsed.crosshair });
    analytics.track("CS2 HUD - Config Imported", {
      hud: Object.keys(parsed.hud).length,
      crosshair: Boolean(parsed.crosshair),
    });
  };

  // Read in the browser and applied straight away, so dropping files is the
  // whole interaction rather than the first half of one.
  const readConfigFiles = async (list: FileList | null) => {
    const files = [...(list ?? [])].filter((file) =>
      CONFIG_FILE.test(file.name),
    );
    const bytes = files.reduce((total, file) => total + file.size, 0);

    if (!files.length) {
      setFileNote("No config files in that selection");
      return;
    }

    if (files.length > MAX_CONFIG_FILES || bytes > MAX_CONFIG_BYTES) {
      setFileNote("That selection is too large, pick the .vcfg files directly");
      return;
    }

    // Adds to what is already here rather than replacing it, so the files can
    // be dropped one at a time. Skipping names we already hold stops a repeat
    // drop from counting a file twice.
    const fresh = files.filter((file) => !loaded.includes(file.name));
    if (!fresh.length) {
      setFileNote("Already added");
      return;
    }

    const texts = await Promise.all(fresh.map((file) => file.text()));
    const combined = [pasted, ...texts].filter(Boolean).join("\n");

    setFileNote("");
    setLoaded([...loaded, ...fresh.map((file) => file.name)]);
    setPasted(combined);
    applyConfig(combined);
    // Open straight onto what we read, so the result is visible without a click.
    setShowDetails(true);
  };

  const matchedQuality =
    quality?.status === "exact" || quality?.status === "closest"
      ? quality.config
      : null;

  // isDirty only tracks the hud draft, so without this a config that changes
  // nothing but the quality would leave Save disabled with work pending.
  const qualityPending = Boolean(
    matchedQuality && matchedQuality.configName !== currentConfig,
  );
  const hasChanges = isDirty || qualityPending;
  const codeError = crosshairCodeError(crosshair.code);

  // Quality lives behind its own mutation, so Save has to commit both. The
  // refetch matters: without it currentConfig stays stale, so the button never
  // settles and a quality-only save looks like nothing happened.
  const handleSave = async () => {
    save();
    if (qualityPending && matchedQuality) {
      const message = "Couldn't save your clip quality. Please try again.";
      try {
        const { data } = await updateConfig({
          variables: { configName: matchedQuality.configName },
        });
        const error = data?.updateClipQualityConfig?.errors?.[0];
        if (error) failSave(message, error.message);
        await refetch();
      } catch (error) {
        failSave(message, error instanceof Error ? error.message : "unknown");
      }
    }
  };

  const clearConfig = () => {
    setQuality(null);
    setLoaded([]);
    setPasted("");
    setImported(null);
    setFileNote("");
  };

  const rangeRow = (range: IRange) => {
    const value = range.value ?? null;

    return (
      <RangeRow key={range.label} $disabled={range.disabled}>
        <SettingInfo>
          <SettingLabel>{range.label}</SettingLabel>
          {range.description && (
            <SettingDescription>{range.description}</SettingDescription>
          )}
        </SettingInfo>
        <RangeControls>
          <RangeValue>{value === null ? "Default" : value}</RangeValue>
          <RangeInput
            type="range"
            aria-label={range.label}
            min={range.min}
            max={range.max}
            step={range.step}
            value={value ?? range.unset}
            disabled={range.disabled}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              range.onChange(Number(e.target.value))
            }
          />
          {range.resettable !== false && (
            <ClearButton
              type="button"
              disabled={value === null}
              onClick={() => range.onChange(null)}
            >
              Reset
            </ClearButton>
          )}
        </RangeControls>
      </RangeRow>
    );
  };

  const hudRange =
    (key: "radarSize" | "mapZoom" | "hudScale") => (value: number | null) =>
      setDraft({ ...draft, [key]: value });

  const runImport = () => applyConfig(pasted);

  const elementRow = (element: IElement) => {
    const disabled = element.dependsOn
      ? !draft[element.dependsOn]
      : !draft.showHud && !element.alwaysEnabled;
    const on = element.keys.every((key) => Boolean(draft[key]));

    const toggle = () => {
      if (disabled) return;

      const patch = element.keys.reduce(
        (acc, key) => ({ ...acc, [key]: !on }),
        {} as Partial<ICs2Hud>,
      );
      setDraft({ ...draft, ...patch });
    };

    return (
      <SettingRow key={element.label} $disabled={disabled}>
        <SettingInfo>
          <SettingLabel>
            {element.icon}
            {element.label}
          </SettingLabel>
          {element.description && (
            <SettingDescription>{element.description}</SettingDescription>
          )}
        </SettingInfo>
        <Toggle
          role="switch"
          aria-checked={on}
          aria-label={element.label}
          $on={on}
          onClick={toggle}
        >
          <ToggleKnob $on={on} />
        </Toggle>
      </SettingRow>
    );
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e: MouseEvent) => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Close">
          <HiX size={20} />
        </CloseButton>

        <Header>
          <GameLogo src={cs2Logo} alt="Counter-Strike" />
          <HeaderText>
            <Title>Counter-Strike Clip Settings</Title>
            <Subtitle>Applies to every Counter-Strike clip you create</Subtitle>
          </HeaderText>
        </Header>

        <TabBar>
          {SECTIONS.map((item) => (
            <NavItem
              key={item.id}
              type="button"
              $active={section === item.id}
              onClick={() => setSection(item.id)}
            >
              {item.icon}
              {item.label}
            </NavItem>
          ))}
        </TabBar>

        {section === "HUD" && (
          <TipBanner type="button" onClick={() => setSection("IMPORT")}>
            <TbBulb size={18} />
            <span>
              <TipLabel>Did you know?</TipLabel> You can import all of your
              settings from your CS2 config files instead of setting it by hand.{" "}
              <TipAction>Click here</TipAction>
            </span>
          </TipBanner>
        )}

        <Layout>
          {section === "HUD" && (
            <PreviewColumn>
              <Preview onMouseMove={zoomToCursor} onMouseLeave={clearZoom}>
                <PreviewStage ref={stage}>
                  <PreviewLayer
                    src={HUD_OFF}
                    alt="Counter-Strike clip preview"
                  />
                  {draft.showHud && (
                    <>
                      <PreviewLayer
                        src={HUD_ON}
                        $clip={REGIONS.healthAmmo}
                        alt=""
                      />
                      {draft.topPanel === "COLLAPSED" && (
                        <PreviewLayer
                          src={HUD_ON}
                          $clip={REGIONS.topCollapsed}
                          alt=""
                        />
                      )}
                      {draft.topPanel === "EXPANDED" && (
                        <PreviewLayer
                          src={HUD_TOP_EXPANDED}
                          $clip={REGIONS.topExpanded}
                          alt=""
                        />
                      )}
                      {draft.radar && (
                        <PreviewLayer
                          src={HUD_ON}
                          $clip={REGIONS.radar}
                          alt=""
                        />
                      )}
                      {draft.money && (
                        <PreviewLayer
                          src={HUD_ON}
                          $clip={REGIONS.money}
                          alt=""
                        />
                      )}
                      {draft.equipment && (
                        <PreviewLayer
                          src={HUD_ON}
                          $clip={REGIONS.equipment}
                          alt=""
                        />
                      )}
                      {draft.chat && (
                        <PreviewLayer
                          src={HUD_ON}
                          $clip={REGIONS.chat}
                          alt=""
                        />
                      )}

                      {/* recolours only the hud pixels, via an alpha mask cut
                          from the difference between the two screenshots. the
                          top panel is team coloured, so it is left alone */}
                      {tint && (
                        <>
                          <PreviewTint
                            $mask={maskHealthAmmo}
                            $inset={REGIONS.healthAmmo}
                            style={{ color: tint }}
                          />
                          {draft.money && (
                            <PreviewTint
                              $mask={maskMoney}
                              $inset={REGIONS.money}
                              style={{ color: tint }}
                            />
                          )}
                          {draft.equipment && (
                            <PreviewTint
                              $mask={maskEquipment}
                              $inset={REGIONS.equipment}
                              style={{ color: tint }}
                            />
                          )}
                        </>
                      )}
                    </>
                  )}
                  {draft.killfeed && (
                    <PreviewLayer
                      src={HUD_ON}
                      $clip={REGIONS.killfeed}
                      alt=""
                    />
                  )}
                </PreviewStage>
              </Preview>
              <PreviewNote>
                Preview image. An approximation of your settings, not a 1:1
                match for your clip. Hover to zoom in.
              </PreviewNote>
            </PreviewColumn>
          )}

          <Controls>
            {section === "HUD" && (
              <>
                <SectionHeader>Display</SectionHeader>
                <SettingRow>
                  <SettingInfo>
                    <SettingLabel>
                      <TbLayoutDashboard size={18} />
                      Show HUD elements
                    </SettingLabel>
                  </SettingInfo>
                  <Toggle
                    role="switch"
                    aria-checked={draft.showHud}
                    aria-label="Show HUD elements"
                    $on={draft.showHud}
                    onClick={() =>
                      setDraft({ ...draft, showHud: !draft.showHud })
                    }
                  >
                    <ToggleKnob $on={draft.showHud} />
                  </Toggle>
                </SettingRow>

                {elementRow(XRAY)}
                <PreviewCaveat>Not shown in the preview</PreviewCaveat>

                <SectionIntro>
                  Presets are a quick starting point. You can still change
                  anything below
                </SectionIntro>
                <PresetRow>
                  {HUD_PRESETS.map((preset) => (
                    <PresetButton
                      key={preset.label}
                      type="button"
                      onClick={() => setDraft({ ...draft, ...preset.values })}
                    >
                      {preset.label}
                    </PresetButton>
                  ))}
                </PresetRow>

                <Divider />

                <SectionHeader>Kill feed</SectionHeader>
                <SectionIntro>
                  Applies even with the HUD turned off
                </SectionIntro>
                {elementRow(KILLFEED)}
                {KILLFEED_OPTIONS.map(elementRow)}

                <Divider />

                <SectionHeader>HUD color</SectionHeader>
                <SectionIntro>Tints your health, ammo and panels</SectionIntro>
                <ColorGrid $disabled={!draft.showHud}>
                  {CS2_HUD_COLORS.map((color, index) => (
                    <ColorSwatch
                      key={color.name}
                      type="button"
                      aria-label={color.name}
                      aria-pressed={draft.color === index}
                      title={color.name}
                      $swatch={color.swatch}
                      $selected={draft.color === index}
                      disabled={!draft.showHud}
                      onClick={() => setDraft({ ...draft, color: index })}
                    />
                  ))}
                </ColorGrid>

                <Divider />

                <SectionHeader>Elements</SectionHeader>
                <SectionIntro>
                  Pick which pieces of the HUD show on your clips
                </SectionIntro>

                <SettingRow $disabled={!draft.showHud}>
                  <SettingInfo>
                    <SettingLabel>
                      <TbLayoutNavbar size={18} />
                      Top panel
                    </SettingLabel>
                  </SettingInfo>
                  <Select
                    aria-label="Top panel"
                    value={draft.topPanel}
                    disabled={!draft.showHud}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setDraft({
                        ...draft,
                        topPanel: e.target.value as ICs2Hud["topPanel"],
                      })
                    }
                  >
                    <option value="EXPANDED">Expanded</option>
                    <option value="COLLAPSED">Collapsed</option>
                    <option value="HIDDEN">Hidden</option>
                  </Select>
                </SettingRow>

                {ELEMENTS.map((element) => (
                  <Fragment key={element.label}>
                    {elementRow(element)}
                    {element.keys[0] === "radar" && (
                      <>
                        {rangeRow({
                          label: "Radar size",
                          description: "How large the radar is drawn on screen",
                          value: draft.radarSize,
                          unset: 1,
                          min: 0.8,
                          max: 1.3,
                          step: 0.05,
                          disabled: !draft.showHud || !draft.radar,
                          onChange: hudRange("radarSize"),
                        })}
                        {rangeRow({
                          label: "Radar zoom",
                          description: "How far the radar map is zoomed in",
                          value: draft.mapZoom,
                          unset: 0.7,
                          min: 0.25,
                          max: 1,
                          step: 0.05,
                          disabled: !draft.showHud || !draft.radar,
                          onChange: hudRange("mapZoom"),
                        })}
                        <PreviewCaveat>Not shown in the preview</PreviewCaveat>
                      </>
                    )}
                  </Fragment>
                ))}

                <Divider />
                <SectionHeader>Sizing</SectionHeader>
                <SectionIntro>
                  Leave one on Default to keep whatever the game already does
                </SectionIntro>

                {rangeRow({
                  label: "HUD scale",
                  description:
                    "Scales the kill feed too, so it applies even with the HUD off",
                  value: draft.hudScale,
                  unset: 0.85,
                  min: 0.5,
                  max: 0.95,
                  step: 0.05,
                  onChange: hudRange("hudScale"),
                })}
                <PreviewCaveat>Not shown in the preview</PreviewCaveat>
              </>
            )}

            {section === "CROSSHAIR" && (
              <>
                <SectionHeader>Crosshair</SectionHeader>
                <SectionIntro>
                  Choose which crosshair is drawn on your Counter-Strike clips
                </SectionIntro>

                {CROSSHAIR_MODES.map((mode) => (
                  <Fragment key={mode.id}>
                    <OptionRow
                      type="button"
                      $selected={crosshair.mode === mode.id}
                      onClick={() =>
                        setCrosshair({ ...crosshair, mode: mode.id })
                      }
                    >
                      <RadioDot
                        data-radio
                        $selected={crosshair.mode === mode.id}
                      />
                      <SettingInfo>
                        <SettingLabel>{mode.label}</SettingLabel>
                        <SettingDescription>
                          {mode.description}
                        </SettingDescription>
                      </SettingInfo>
                    </OptionRow>

                    {mode.id === "CODE" && crosshair.mode === "CODE" && (
                      <CodeField>
                        <CodeInput
                          value={crosshair.code ?? ""}
                          placeholder="CSxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setCrosshair({
                              ...crosshair,
                              code: e.target.value.trim(),
                            })
                          }
                        />
                        <CodeHint $error={Boolean(codeError)}>
                          {codeError ??
                            "In CS2, go to Settings, Crosshair / Scopes, then Share to copy your code"}
                        </CodeHint>
                      </CodeField>
                    )}
                  </Fragment>
                ))}
              </>
            )}

            {section === "IMPORT" && (
              <>
                <SectionIntro $bright>
                  We will read your HUD, crosshair and resolution from these
                  files. Locate them and drop them below
                </SectionIntro>

                <FileRow>
                  <TbFileText size={15} />
                  {CONFIG_FILES.map((file) => (
                    <FileName key={file}>{file}</FileName>
                  ))}
                </FileRow>

                <CollapseHeader
                  type="button"
                  onClick={() => setShowWhere(!showWhere)}
                >
                  {showWhere ? (
                    <TbChevronDown size={14} />
                  ) : (
                    <TbChevronRight size={14} />
                  )}
                  Where are my config files?
                </CollapseHeader>

                {showWhere && (
                  <>
                    {CONFIG_LOCATIONS.filter(
                      (location) =>
                        allOs || !detectOs() || location.os === detectOs(),
                    ).map((location) => (
                      <LocationRow key={location.os}>
                        <LocationOs>
                          {location.icon}
                          {location.os}
                        </LocationOs>
                        <LocationPath>
                          {withPlaceholder(location.path)}
                        </LocationPath>
                      </LocationRow>
                    ))}

                    <LocationNote>
                      Can&apos;t find Steam? Right click Counter-Strike 2,
                      Manage, Browse local files, then go up to the Steam
                      folder. If you see several id folders, open the one with a
                      730 inside.
                    </LocationNote>

                    {!allOs && detectOs() && (
                      <PasteToggle type="button" onClick={() => setAllOs(true)}>
                        Show other systems
                      </PasteToggle>
                    )}
                  </>
                )}

                <Divider />

                <DropZone
                  $active={dragging}
                  onDragEnter={(e: DragEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragOver={(e: DragEvent<HTMLDivElement>) =>
                    e.preventDefault()
                  }
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e: DragEvent<HTMLDivElement>) => {
                    e.preventDefault();
                    setDragging(false);
                    readConfigFiles(e.dataTransfer.files);
                  }}
                >
                  <TbUpload size={28} />
                  <DropTitle>Drag & Drop your CS2 config files here</DropTitle>
                  <DropHint>An autoexec.cfg works too if you use one</DropHint>
                  <ImportActions>
                    <ImportFileButton>
                      <TbFolderOpen size={16} />
                      Choose files
                      <input
                        type="file"
                        multiple
                        accept=".vcfg,.cfg,.txt"
                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                          readConfigFiles(e.target.files)
                        }
                      />
                    </ImportFileButton>
                  </ImportActions>
                  {loaded.length > 0 && (
                    <LoadedFiles>
                      <span>
                        {loaded.length > 3
                          ? `${loaded.length} files added`
                          : loaded.join(", ")}
                      </span>
                      <ClearButton type="button" onClick={clearConfig}>
                        Clear
                      </ClearButton>
                    </LoadedFiles>
                  )}
                  {!imported && fileNote && (
                    <ImportResult $found={fileNote.startsWith("Loaded")}>
                      {fileNote}
                    </ImportResult>
                  )}
                  {imported && (
                    <ImportResult $found={importedCount > 0}>
                      {importedCount === 0 && <TbAlertTriangle size={16} />}
                      {importedCount > 0
                        ? `Imported ${importedCount} ${
                            importedCount === 1 ? "setting" : "settings"
                          }, save to keep them`
                        : "We couldn't find anything supported in those files, try uploading another"}
                    </ImportResult>
                  )}
                  <PasteToggle
                    type="button"
                    onClick={() => setShowDetails(!showDetails)}
                  >
                    {showDetails
                      ? "Hide"
                      : loaded.length
                      ? "Preview combined config"
                      : "or paste the contents"}
                  </PasteToggle>
                </DropZone>

                {showDetails && loaded.length > 0 && (
                  <ImportSection>
                    <SectionHeader>What we found</SectionHeader>
                    <MatchedConvars>
                      {imported?.matched.length
                        ? imported.matched.map((line) => {
                            const [convar, raw] = line.split(" ");
                            const readback = CONVAR_READBACK[convar];

                            return (
                              <MatchedRow key={convar}>
                                <MatchedLabel>
                                  <TbCheck size={15} />
                                  <span>{readback?.label ?? convar}</span>
                                </MatchedLabel>
                                <MatchedValue>
                                  {readback ? readback.value(Number(raw)) : raw}
                                </MatchedValue>
                              </MatchedRow>
                            );
                          })
                        : null}
                      {!imported?.matched.length &&
                        !imported?.crosshair &&
                        !imported?.resolution && (
                          <MatchedEmpty>
                            <TbAlertTriangle size={16} />
                            We couldn&apos;t find anything supported in those
                            files, try uploading another
                          </MatchedEmpty>
                        )}
                      {quality?.status === "exact" && (
                        <MatchedRow>
                          <MatchedLabel>
                            <TbCheck size={15} />
                            <span>Clip quality</span>
                          </MatchedLabel>
                          <MatchedValue>
                            {quality.config.settings.resolution}{" "}
                            {quality.config.settings.framerate}fps
                          </MatchedValue>
                        </MatchedRow>
                      )}
                      {quality?.status === "closest" && (
                        <MatchedRow>
                          <MatchedLabel>
                            <TbCheck size={15} />
                            <span>Clip quality, closest we render</span>
                          </MatchedLabel>
                          <MatchedValue>
                            {quality.config.settings.resolution}{" "}
                            {quality.config.settings.framerate}fps
                          </MatchedValue>
                        </MatchedRow>
                      )}
                      {quality?.status === "tier" && (
                        <MatchedEmpty>
                          <TbAlertTriangle size={16} />
                          {quality.resolution} needs{" "}
                          {TIER_DISPLAY[quality.requiredUserGroup] ??
                            quality.requiredUserGroup}
                          , so we left your clip quality alone
                        </MatchedEmpty>
                      )}
                      {quality?.status === "none" && (
                        <MatchedEmpty>
                          <TbAlertTriangle size={16} />
                          We do not render your aspect ratio yet, so we left
                          your clip quality alone
                        </MatchedEmpty>
                      )}
                      {imported?.crosshair && (
                        <MatchedRow>
                          <MatchedLabel>
                            <TbCheck size={15} />
                            <span>Crosshair</span>
                          </MatchedLabel>
                          <MatchedValue>My crosshair</MatchedValue>
                        </MatchedRow>
                      )}
                    </MatchedConvars>
                  </ImportSection>
                )}

                {showDetails && loaded.length === 0 && (
                  <ImportSection>
                    <ImportArea
                      aria-label="CS2 config"
                      value={pasted}
                      placeholder="Paste the contents of a .vcfg file, or drop it above"
                      onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                        setPasted(e.target.value)
                      }
                    />
                    <ImportActions>
                      <ImportButton
                        type="button"
                        onClick={runImport}
                        disabled={!pasted.trim()}
                      >
                        <TbFileImport size={16} />
                        Import your Settings
                      </ImportButton>
                    </ImportActions>
                  </ImportSection>
                )}
              </>
            )}
          </Controls>
        </Layout>

        {isLoggedIn && (
          <Footer>
            {onAllSettings && (
              <AllSettingsLink onClick={onAllSettings}>
                <HiChevronLeft size={14} />
                All Clipping Settings
              </AllSettingsLink>
            )}
            {saveStatus !== "idle" && (
              <SaveFeedback $success={saveStatus === "saved"}>
                {saveStatus === "saved" ? "Saved" : saveError}
              </SaveFeedback>
            )}
            <SaveButton onClick={handleSave} disabled={saving || !hasChanges}>
              {saving
                ? "Saving..."
                : hasChanges
                ? "Save Settings"
                : "Nothing to save"}
            </SaveButton>
          </Footer>
        )}
      </ModalContainer>
    </ModalOverlay>
  );
};
